import type { Plan } from "@roxpacer/core";
import { create } from "zustand";
import { usePlans } from "../store";
import { ApiError, type PlansApi } from "./api";
import { mergePlans } from "./merge";

export type SyncStatus = "off" | "syncing" | "synced" | "offline" | "error";

export const useSyncStatus = create<{ status: SyncStatus; message: string | null }>(() => ({
  status: "off",
  message: null,
}));

const setStatus = (status: SyncStatus, message: string | null = null) =>
  useSyncStatus.setState({ status, message });

export interface SyncOptions {
  /** Wait after the last edit before saving. */
  debounceMs: number;
  /** Retry delay after a failed save. */
  retryMs: number;
}

/**
 * Keeps the plans store in sync with the account: a full merge on start (and when
 * the tab regains focus or the browser comes online), then debounced saves of each
 * changed plan. Network operations run one at a time.
 */
export class SyncEngine {
  private readonly pending = new Set<string>();
  private chain: Promise<void> = Promise.resolve();
  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private applyingRemote = false;
  private unsubscribe: (() => void) | undefined;
  private running = false;

  constructor(
    private readonly api: PlansApi,
    private readonly options: SyncOptions = { debounceMs: 1000, retryMs: 30_000 },
  ) {}

  start(): Promise<void> {
    if (this.running) return this.chain;
    this.running = true;
    this.unsubscribe = usePlans.subscribe((state, prev) => {
      if (this.applyingRemote) return;
      const previous = new Map(prev.plans.map((p) => [p.id, p]));
      for (const plan of state.plans) {
        if (previous.get(plan.id) !== plan) this.pending.add(plan.id);
      }
      if (this.pending.size > 0 || state.deletedIds !== prev.deletedIds) this.scheduleSave();
    });
    window.addEventListener("online", this.onReconnect);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    return this.enqueue(() => this.fullSync({ atSignIn: true }));
  }

  stop(): void {
    this.running = false;
    this.unsubscribe?.();
    clearTimeout(this.saveTimer);
    clearTimeout(this.retryTimer);
    window.removeEventListener("online", this.onReconnect);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.pending.clear();
    setStatus("off");
  }

  /** Saves pending changes now instead of waiting for the debounce. */
  flush(): Promise<void> {
    clearTimeout(this.saveTimer);
    return this.enqueue(() => this.save());
  }

  private readonly onReconnect = () => {
    if (this.running) void this.enqueue(() => this.fullSync({ atSignIn: false }));
  };

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === "visible") this.onReconnect();
  };

  private scheduleSave(delay = this.options.debounceMs) {
    if (!this.running) return;
    setStatus("syncing");
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.enqueue(() => this.save()), delay);
  }

  private enqueue(operation: () => Promise<void>): Promise<void> {
    this.chain = this.chain.then(async () => {
      if (!this.running) return;
      try {
        await operation();
      } catch (error) {
        this.onError(error);
      }
    });
    return this.chain;
  }

  private async fullSync(options: { atSignIn: boolean }) {
    setStatus("syncing");
    await this.saveDeletions();
    const remote = await this.api.list();
    if (!this.running) return;
    const { plans, toUpload } = mergePlans(usePlans.getState().plans, remote, options);
    this.applyRemote(() => usePlans.getState().replacePlans(plans));
    for (const plan of toUpload) this.pending.add(plan.id);
    await this.save();
  }

  private async save() {
    await this.saveDeletions();
    for (const id of [...this.pending]) {
      const plan = usePlans.getState().plans.find((p) => p.id === id);
      this.pending.delete(id);
      if (!plan) continue;
      try {
        const result = await this.api.put(plan);
        if (result.status === "conflict") this.replaceWithRemote(result.plan);
      } catch (error) {
        // Retry later unless the request itself is the problem.
        if (!(error instanceof ApiError && (error.status === 400 || error.status === 422))) {
          this.pending.add(id);
        }
        throw error;
      }
    }
    if (this.running) setStatus("synced");
  }

  private async saveDeletions() {
    for (const id of usePlans.getState().deletedIds) {
      await this.api.delete(id);
      usePlans.getState().clearDeleted([id]);
    }
  }

  private replaceWithRemote(remote: Plan) {
    this.applyRemote(() => {
      const { plans, replacePlans } = usePlans.getState();
      replacePlans(plans.map((p) => (p.id === remote.id ? remote : p)));
    });
  }

  private applyRemote(update: () => void) {
    this.applyingRemote = true;
    try {
      update();
    } finally {
      this.applyingRemote = false;
    }
  }

  private onError(error: unknown) {
    if (!this.running) return;
    if (error instanceof ApiError) {
      if (error.status === 401) {
        setStatus("error", "Your session has expired. Sign in again to keep syncing.");
        return;
      }
      setStatus("error", error.message);
    } else {
      // fetch rejects with a TypeError when the network is unreachable.
      setStatus("offline", "Offline. Changes will sync when you're back online.");
    }
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.onReconnect(), this.options.retryMs);
  }
}
